import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { formatDateShort } from '../../../core/components/ui/utils/formatters';

/**
 * FAQ Card Component (Mobile View)
 *
 * Displays a FAQ entry in a card layout optimized for mobile devices.
 * Shows question, answer preview, creator, and action buttons.
 *
 * @param {Object} props
 * @param {Object} props.faq - FAQ object
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 *
 * @example
 * <FAQCard
 *   faq={faq}
 *   onEdit={() => handleEdit(faq)}
 *   onDelete={() => handleDelete(faq)}
 * />
 */
export function FAQCard({ faq, onEdit, onDelete }) {
  const { t } = useTranslation();

  // Format creator name
  const formatCreator = (createdBy) => {
    if (!createdBy) return t('faq.unknownCreator', 'Unknown');
    if (createdBy.first_name && createdBy.last_name) {
      return `${createdBy.first_name} ${createdBy.last_name}`;
    }
    return createdBy.username || t('faq.unknownCreator', 'Unknown');
  };

  return (
    <div className="border border-border-default rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Question */}
      <div className="mb-3">
        <p className="text-xs font-medium text-muted uppercase mb-1">
          {t('faq.question', 'Question')}
        </p>
        <h3 className="text-base font-semibold text-default break-words line-clamp-2">
          {faq.question}
        </h3>
      </div>

      {/* Answer */}
      <div className="mb-3">
        <p className="text-xs font-medium text-muted uppercase mb-1">
          {t('faq.answer', 'Answer')}
        </p>
        <p className="text-sm text-subtle break-words line-clamp-3">
          {faq.answer}
        </p>
      </div>

      {/* Metadata */}
      <div className="flex items-center justify-between text-xs text-muted mb-4 pb-3 border-b border-border-subtle">
        <span>{formatCreator(faq.created_by)}</span>
        <span>{formatDateShort(faq.time_created)}</span>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        {/* Edit Button */}
        <button
          type="button"
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-border-default text-brand hover:bg-brand/5 focus:outline-none focus:bg-brand/5 cursor-pointer"
          onClick={onEdit}
          aria-label={t('faq.edit', 'Edit')}
        >
          <Icon icon="lucide:edit" className="size-4" />
          <span>{t('faq.edit', 'Edit')}</span>
        </button>

        {/* Delete Button */}
        <button
          type="button"
          className="inline-flex items-center justify-center size-10 text-sm font-medium rounded-lg border border-border-default text-error hover:bg-error/5 focus:outline-none focus:bg-error/5 cursor-pointer"
          onClick={onDelete}
          aria-label={t('faq.delete', 'Delete')}
        >
          <Icon icon="lucide:trash-2" className="size-4" />
        </button>
      </div>
    </div>
  );
}

FAQCard.propTypes = {
  faq: PropTypes.shape({
    id: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    answer: PropTypes.string.isRequired,
    time_created: PropTypes.string.isRequired,
    created_by: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      username: PropTypes.string,
    }),
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
